import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useMotionValue, useMotionValueEvent, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useStore } from "@/context/StoreContext";

const U = (id) => (id.startsWith("/") ? id : `https://images.unsplash.com/${id}?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400`);
const SCENES = [
  { key: "STUDIO", bg: "#F4CF3F", fg: "#1C1A19", img: "/img/hero-lavender.webp", model: "Lavender Study", to: "/shop", n: "01" },
  { key: "LAVENDER", bg: "#B794F6", fg: "#1C1A19", img: "photo-1656335362192-2bc9051b1824", model: "Air Jordan 4", to: "/collections/jordan-retro", n: "02" },
  { key: "NIGHT", bg: "#1C1A19", fg: "#F4CF3F", img: "photo-1552346154-21d32810aba3", model: "Air Jordan 1", to: "/collections/air-jordan", n: "03" },
  { key: "GOLD", bg: "#E6BF2E", fg: "#1C1A19", img: "photo-1600185365483-26d7a4cc7519", model: "Air Max", to: "/collections/nike", n: "04" },
];

export function Showcase() {
  const { t } = useStore();
  const ref = useRef(null);
  const [idx, setIdx] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  useMotionValueEvent(scrollYProgress, "change", (v) => setIdx(Math.min(SCENES.length - 1, Math.floor(v * SCENES.length))));
  const bg = useTransform(scrollYProgress, [0, 0.25, 0.3, 0.5, 0.55, 0.75, 0.8, 1], ["#F4CF3F", "#F4CF3F", "#B794F6", "#B794F6", "#1C1A19", "#1C1A19", "#E6BF2E", "#E6BF2E"]);
  const bar = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-18, 18]), { stiffness: 120, damping: 14 });
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [14, -14]), { stiffness: 120, damping: 14 });
  const glareX = useTransform(mx, [-0.5, 0.5], ["0%", "100%"]);
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };
  const s = SCENES[idx];

  return (
    <section ref={ref} className="relative h-[400vh]" data-testid="showcase-section">
      <motion.div style={{ backgroundColor: bg }} className="sticky top-0 flex h-screen items-center overflow-hidden">
        <div className={`absolute inset-0 transition-opacity duration-700 ${idx === 1 ? "opacity-100" : "opacity-0"} grid-lines`} />
        <div className={`absolute left-1/2 top-1/2 h-[70vh] w-[70vh] -translate-x-1/2 -translate-y-1/2 rounded-full bg-sun/10 blur-[120px] transition-opacity duration-700 ${idx === 2 ? "opacity-100" : "opacity-0"}`} />
        <AnimatePresence mode="wait">
          <motion.p key={s.key} initial={{ opacity: 0, x: 80 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -80 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            style={{ "--stroke": s.fg }} className="text-outline pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 select-none whitespace-nowrap text-center font-display text-[22vw] font-black leading-none tracking-tighter opacity-30">
            {s.key}
          </motion.p>
        </AnimatePresence>

        <div className="container-x relative z-10 grid h-full grid-cols-1 items-center gap-8 pb-12 pt-24 lg:grid-cols-12">
          <div className="lg:col-span-4" style={{ color: s.fg }}>
            <p className="eyebrow opacity-60">{t("showcaseEyebrow")}</p>
            <AnimatePresence mode="wait">
              <motion.div key={s.key} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.5 }}>
                <p className="mt-6 font-mono text-sm opacity-50">{s.n} / 04 — {s.key}</p>
                <h2 className="mt-3 font-display text-5xl font-black uppercase leading-[0.9] tracking-tight lg:text-7xl" data-testid="showcase-model">{s.model}</h2>
                <Link to={s.to} className="link-u mt-8 inline-flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.2em]" data-testid="showcase-shop-link">
                  {t("shopNow")} <ArrowUpRight className="h-4 w-4" />
                </Link>
              </motion.div>
            </AnimatePresence>
            <p className="mt-12 hidden text-sm opacity-50 lg:block">{t("showcaseHint")}</p>
          </div>

          <div className="flex justify-center lg:col-span-8" style={{ perspective: 1200 }}>
            <motion.div onMouseMove={onMove} onMouseLeave={reset} style={{ rotateX: rotX, rotateY: rotY, transformStyle: "preserve-3d" }}
              className="relative aspect-[5/4] w-full max-w-[min(700px,calc((100vh-14rem)*1.25))] cursor-grab" data-testid="showcase-tilt">
              <AnimatePresence mode="popLayout">
                <motion.img key={s.img} src={U(s.img)} alt={s.model}
                  initial={{ opacity: 0, scale: 0.9, rotate: -4, filter: "blur(10px)" }} animate={{ opacity: 1, scale: 1, rotate: 0, filter: "blur(0px)" }}
                  exit={{ opacity: 0, scale: 1.05, rotate: 3, filter: "blur(10px)" }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute inset-0 h-full w-full object-cover shadow-[0_80px_120px_-40px_rgba(0,0,0,0.55)]" style={{ transform: "translateZ(40px)" }} />
              </AnimatePresence>
              <motion.div style={{ left: glareX }} className="pointer-events-none absolute inset-y-0 w-1/3 -translate-x-1/2 bg-gradient-to-r from-transparent via-sun/20 to-transparent mix-blend-overlay" />
            </motion.div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 h-px w-48 -translate-x-1/2 bg-current opacity-20" style={{ color: s.fg }} />
        <motion.div style={{ width: bar, backgroundColor: s.fg }} className="absolute bottom-8 left-[calc(50%-6rem)] h-px max-w-48" />
      </motion.div>
    </section>
  );
}
